/**
 * Admin Timesheets — rich management screen for all investigator time entries.
 *
 * Capabilities:
 *   • Filter by investigator, case, date range, status, approval status.
 *   • View every entry with investigator + case metadata.
 *   • Edit any entry (clock in/out, activity, notes, billable) with audit trail.
 *   • Bulk approve selected rows; Approve / Deny individually (mandatory reason).
 *   • Export filtered timesheet PDF.
 *   • Email the per-case timesheet PDF to the case client.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  Clock, CheckCircle2, XCircle, Edit2, Mail, Download, Filter, RefreshCw,
  User, FileText, History, Loader2,
} from 'lucide-react';

const fmtHMS = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};
const dt = (v) => (v ? new Date(v).toLocaleString() : '—');
const toLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};

const APPROVAL_TONES = {
  APPROVED: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  DENIED:   'bg-red-500/20 text-red-300 border-red-500/40',
  PENDING:  'bg-amber-500/20 text-amber-300 border-amber-500/40',
};

export default function AdminTimesheets() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
  const H = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const JH = useMemo(() => ({ ...H, 'Content-Type': 'application/json' }), [H]);

  const [filters, setFilters] = useState({
    investigator_id: '', case_id: '', status: '', approval_status: 'PENDING',
    date_from: '', date_to: '',
  });
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [users, setUsers] = useState([]);
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(null);        // entry being edited
  const [denyFor, setDenyFor] = useState(null);        // entry being denied
  const [denyReason, setDenyReason] = useState('');
  const [emailFor, setEmailFor] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('');

  const loadLookups = useCallback(async () => {
    const [u, c] = await Promise.all([
      fetch(`${BACKEND_URL}/api/users`, { headers: H }).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${BACKEND_URL}/api/cases`, { headers: H }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]);
    setUsers(Array.isArray(u) ? u : (u?.users || []));
    setCases(Array.isArray(c) ? c : (c?.cases || []));
  }, [H]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await fetch(`${BACKEND_URL}/api/admin/timesheets?${params.toString()}`, { headers: H });
      const d = r.ok ? await r.json() : { entries: [], totals: {} };
      setRows(d.entries || []);
      setTotals(d.totals || {});
      setSelected(new Set());
    } finally { setLoading(false); }
  }, [filters, H]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    const eligible = rows.filter(r => r.status === 'COMPLETED' && r.approval_status !== 'APPROVED');
    if (selected.size === eligible.length) setSelected(new Set());
    else setSelected(new Set(eligible.map(r => r.id)));
  };

  const approveOne = async (id) => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/time-entries/${id}/approve`, { method: 'POST', headers: JH });
      if (r.ok) { toast({ title: 'Approved' }); await load(); }
      else toast({ title: 'Approve failed', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const bulkApprove = async () => {
    if (!selected.size) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/timesheets/bulk-approve`, {
        method: 'POST', headers: JH, body: JSON.stringify({ ids: Array.from(selected) }),
      });
      const d = await r.json();
      if (r.ok) { toast({ title: `Approved ${d.approved} entries` }); await load(); }
      else toast({ title: 'Bulk approve failed', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const submitDeny = async () => {
    if (!denyFor) return;
    if (!denyReason.trim()) { toast({ title: 'Reason required', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/time-entries/${denyFor.id}/deny`, {
        method: 'POST', headers: JH, body: JSON.stringify({ reason: denyReason.trim() }),
      });
      if (r.ok) { toast({ title: 'Denied — investigator notified' }); setDenyFor(null); setDenyReason(''); await load(); }
      else toast({ title: 'Deny failed', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const payload = {
        activity: editing.activity,
        notes: editing.notes,
        billable: !!editing.billable,
        start_time: editing._start ? new Date(editing._start).toISOString() : editing.start_time,
        end_time: editing._end ? new Date(editing._end).toISOString() : editing.end_time,
      };
      // Recompute duration if both ends supplied
      if (payload.start_time && payload.end_time) {
        const diff = (new Date(payload.end_time) - new Date(payload.start_time)) / 1000;
        payload.duration_seconds = Math.max(0, Math.floor(diff) - (editing.paused_seconds || 0));
      }
      const r = await fetch(`${BACKEND_URL}/api/time-entries/${editing.id}`, {
        method: 'PATCH', headers: JH, body: JSON.stringify(payload),
      });
      if (r.ok) { toast({ title: 'Saved' }); setEditing(null); await load(); }
      else { const d = await r.json(); toast({ title: 'Save failed', description: d.detail || '', variant: 'destructive' }); }
    } finally { setBusy(false); }
  };

  const emailClient = async () => {
    if (!emailFor) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/timesheets/${emailFor.id}/email-client`, {
        method: 'POST', headers: JH,
        body: JSON.stringify({ recipient: emailTo.trim() || undefined, note: emailNote.trim() || undefined }),
      });
      const d = await r.json();
      if (r.ok) { toast({ title: `Sent to ${d.to}` }); setEmailFor(null); setEmailTo(''); setEmailNote(''); }
      else toast({ title: 'Email failed', description: d.detail || '', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const exportPdf = () => {
    if (!filters.investigator_id && !filters.case_id) {
      toast({ title: 'Pick a case or investigator first', variant: 'destructive' });
      return;
    }
    const params = new URLSearchParams();
    if (filters.investigator_id) params.set('investigator_id', filters.investigator_id);
    if (filters.case_id) params.set('case_id', filters.case_id);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    const url = `${BACKEND_URL}/api/timesheets/export-pdf?${params.toString()}`;
    // Open with token in a one-shot blob
    fetch(url, { headers: H })
      .then(async r => {
        if (!r.ok) throw new Error('export failed');
        const blob = await r.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `timesheet-${filters.case_id || filters.investigator_id}.pdf`;
        a.click();
      })
      .catch(() => toast({ title: 'Export failed', variant: 'destructive' }));
  };

  const eligibleCount = rows.filter(r => r.status === 'COMPLETED' && r.approval_status !== 'APPROVED').length;

  return (
    <div className="space-y-5" data-testid="admin-timesheets-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
            <Clock className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <div className="text-xs text-blue-400 uppercase tracking-wider font-mono">Admin · Time Clock</div>
            <h1 className="text-2xl font-bold text-white">Timesheet Editor</h1>
            <p className="text-slate-400 text-sm">Review, edit, approve or deny every investigator time entry.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="border-slate-600 !text-slate-200" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={exportPdf} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="export-pdf-btn">
            <Download className="w-4 h-4 mr-1" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Investigator</label>
            <Select value={filters.investigator_id || 'all'} onValueChange={v => setFilters(f => ({ ...f, investigator_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="filter-investigator"><SelectValue placeholder="Everyone" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name || u.name || u.username}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Case</label>
            <Select value={filters.case_id || 'all'} onValueChange={v => setFilters(f => ({ ...f, case_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="filter-case"><SelectValue placeholder="All cases" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cases</SelectItem>
                {cases.map(c => <SelectItem key={c.id} value={c.id}>{c.case_number} — {c.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Status</label>
            <Select value={filters.status || 'any'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'any' ? '' : v }))}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="filter-status"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="PAUSED">Paused</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Approval</label>
            <Select value={filters.approval_status || 'any'} onValueChange={v => setFilters(f => ({ ...f, approval_status: v === 'any' ? '' : v }))}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="filter-approval"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="DENIED">Denied</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">From</label>
            <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className="bg-slate-800 border-slate-600 text-white" data-testid="filter-date-from" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">To</label>
            <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className="bg-slate-800 border-slate-600 text-white" data-testid="filter-date-to" />
          </div>
        </CardContent>
      </Card>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Entries', value: totals.entries ?? 0, tone: 'text-white' },
          { label: 'Total Time', value: fmtHMS(totals.total_seconds), tone: 'text-blue-300' },
          { label: 'Billable', value: fmtHMS(totals.billable_seconds), tone: 'text-emerald-300' },
          { label: 'Pending', value: totals.pending ?? 0, tone: 'text-amber-300' },
          { label: 'Approved', value: totals.approved ?? 0, tone: 'text-emerald-300' },
        ].map(k => (
          <div key={k.label} className="bg-[#131A2A] border border-[#334155] rounded-lg px-3 py-2">
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">{k.label}</div>
            <div className={`text-xl font-bold ${k.tone}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Bulk toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-slate-400">
          {selected.size > 0 ? `${selected.size} selected · ` : ''}
          {eligibleCount} eligible for approval
        </div>
        <Button
          disabled={!selected.size || busy}
          onClick={bulkApprove}
          className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
          data-testid="bulk-approve-btn"
        >
          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve selected ({selected.size})
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardContent className="p-0 overflow-x-auto">
          {rows.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">No time entries match the current filters.</div>
          ) : (
            <table className="w-full text-sm text-slate-200">
              <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="p-2 text-left">
                    <Checkbox
                      checked={selected.size > 0 && selected.size === eligibleCount}
                      onCheckedChange={toggleAll}
                      data-testid="select-all"
                    />
                  </th>
                  <th className="p-2 text-left">Investigator</th>
                  <th className="p-2 text-left">Case</th>
                  <th className="p-2 text-left">Activity</th>
                  <th className="p-2 text-left">Start</th>
                  <th className="p-2 text-left">End</th>
                  <th className="p-2 text-left">Duration</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(e => {
                  const approval = (e.approval_status || (e.status === 'COMPLETED' ? 'PENDING' : '')).toUpperCase();
                  const canSelect = e.status === 'COMPLETED' && e.approval_status !== 'APPROVED';
                  return (
                    <tr key={e.id} className="border-t border-slate-800 hover:bg-slate-900/30" data-testid={`row-${e.id}`}>
                      <td className="p-2">
                        {canSelect && (
                          <Checkbox
                            checked={selected.has(e.id)}
                            onCheckedChange={() => toggleOne(e.id)}
                            data-testid={`row-check-${e.id}`}
                          />
                        )}
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1"><User className="w-3 h-3 text-slate-400" />{e.investigator_name}</div>
                      </td>
                      <td className="p-2 text-xs">{e.case_number}</td>
                      <td className="p-2 text-xs">{(e.activity || '').replace(/_/g, ' ')}</td>
                      <td className="p-2 text-xs">{dt(e.start_time)}</td>
                      <td className="p-2 text-xs">{dt(e.end_time)}</td>
                      <td className="p-2 font-mono">{fmtHMS(e.live_duration_seconds)}</td>
                      <td className="p-2">
                        <Badge className="text-[10px] bg-slate-700 text-slate-200 mr-1">{e.status}</Badge>
                        {approval && <Badge className={`text-[10px] border ${APPROVAL_TONES[approval] || 'bg-slate-700'}`}>{approval}</Badge>}
                      </td>
                      <td className="p-2 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" className="border-slate-600 !text-slate-200 h-7 px-2"
                            onClick={() => setEditing({ ...e, _start: toLocalInput(e.start_time), _end: toLocalInput(e.end_time) })}
                            data-testid={`edit-${e.id}`}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          {canSelect && (
                            <>
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 px-2"
                                onClick={() => approveOne(e.id)} disabled={busy}
                                data-testid={`approve-${e.id}`}>
                                <CheckCircle2 className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="outline" className="border-red-500/40 !text-red-300 h-7 px-2"
                                onClick={() => { setDenyFor(e); setDenyReason(''); }}
                                data-testid={`deny-${e.id}`}>
                                <XCircle className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="outline" className="border-indigo-500/40 !text-indigo-300 h-7 px-2"
                            onClick={() => { setEmailFor(e); setEmailTo(''); setEmailNote(''); }}
                            data-testid={`email-${e.id}`}>
                            <Mail className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0F172A] border-slate-700 text-white max-w-xl" data-testid="edit-dialog">
          <DialogHeader><DialogTitle>Edit time entry</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="text-xs text-slate-400">
                <b>{editing.investigator_name}</b> · Case {editing.case_number}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400">Start</label>
                  <Input type="datetime-local" value={editing._start || ''}
                    onChange={(e) => setEditing(s => ({ ...s, _start: e.target.value }))}
                    className="bg-slate-800 border-slate-600 text-white" data-testid="edit-start" />
                </div>
                <div>
                  <label className="text-xs text-slate-400">End</label>
                  <Input type="datetime-local" value={editing._end || ''}
                    onChange={(e) => setEditing(s => ({ ...s, _end: e.target.value }))}
                    className="bg-slate-800 border-slate-600 text-white" data-testid="edit-end" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400">Activity</label>
                <Input value={editing.activity || ''} onChange={(e) => setEditing(s => ({ ...s, activity: e.target.value.toUpperCase() }))}
                  className="bg-slate-800 border-slate-600 text-white" data-testid="edit-activity" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Notes</label>
                <Textarea rows={3} value={editing.notes || ''}
                  onChange={(e) => setEditing(s => ({ ...s, notes: e.target.value }))}
                  className="bg-slate-800 border-slate-600 text-white" data-testid="edit-notes" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <Checkbox checked={!!editing.billable} onCheckedChange={(v) => setEditing(s => ({ ...s, billable: !!v }))} data-testid="edit-billable" />
                Billable
              </label>
              {editing.edit_history?.length > 0 && (
                <div className="mt-2 text-xs">
                  <div className="flex items-center gap-1 text-slate-400 mb-1"><History className="w-3 h-3" /> Audit trail</div>
                  <ul className="space-y-1 max-h-32 overflow-y-auto border border-slate-800 rounded p-2 bg-slate-900/40">
                    {editing.edit_history.map((h, i) => (
                      <li key={i} className="text-slate-400">
                        {dt(h.at)} · <b>{h.by}</b> · {Object.keys(h.changes || {}).filter(k => k !== 'updated_at' && k !== 'edit_history').join(', ') || '—'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-slate-600 !text-slate-200" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="save-edit-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deny dialog */}
      <Dialog open={!!denyFor} onOpenChange={(o) => !o && setDenyFor(null)}>
        <DialogContent className="bg-[#0F172A] border-slate-700 text-white max-w-md" data-testid="deny-dialog">
          <DialogHeader><DialogTitle>Deny time entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-400">
              The investigator will receive an email + in-app notification with your reason.
            </div>
            <Textarea rows={4} value={denyReason} onChange={(e) => setDenyReason(e.target.value)}
              placeholder="Reason (required)…" className="bg-slate-800 border-slate-600 text-white" data-testid="deny-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-600 !text-slate-200" onClick={() => setDenyFor(null)}>Cancel</Button>
            <Button onClick={submitDeny} disabled={busy || !denyReason.trim()} className="bg-red-600 hover:bg-red-500 text-white" data-testid="submit-deny">
              Deny & notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email dialog */}
      <Dialog open={!!emailFor} onOpenChange={(o) => !o && setEmailFor(null)}>
        <DialogContent className="bg-[#0F172A] border-slate-700 text-white max-w-md" data-testid="email-dialog">
          <DialogHeader><DialogTitle>Email timesheet to client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-400">
              Attaches a PDF with every <b>completed</b> time entry for case <b>{emailFor?.case_number}</b>.
              Leave the recipient blank to use the case's primary client email.
            </div>
            <Input placeholder="recipient@client.com (optional)" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" data-testid="email-to" />
            <Textarea rows={3} placeholder="Optional note" value={emailNote} onChange={(e) => setEmailNote(e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" data-testid="email-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-600 !text-slate-200" onClick={() => setEmailFor(null)}>Cancel</Button>
            <Button onClick={emailClient} disabled={busy} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="submit-email">
              <Mail className="w-4 h-4 mr-1" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
